import { useState, useEffect, useRef } from 'react';
import { Button, Modal, Stack, Form, Image } from 'react-bootstrap';
import Calendar from 'react-calendar';
import Slider from 'react-slick';
import { useGetSeekerAvailabilityQuery, useGetTimeSlotsQuery } from 'services';
import useCommonStore from 'store/common';
import { formatDateForMeeting, S3_URL } from 'constants/index';
import useAuthStore from 'store/auth';
import moment from 'moment';
import { formatAvailableDates, isTimeGone } from 'utils';
import useTranslate from 'hooks/useTranslate';
import { IMAGE_PATHS } from 'constants/imagePaths';

const ScheduleMeeting: React.FC<any> = ({ showModal }: any) => {
	const { translate } = useTranslate();
	const { modalConfig = {}, hideCommonModal }: any = useCommonStore((state) => state);
	const {
		user: { firstName, lastName, profilePicture },
	}: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});

	const { data: { mettingTimeSloat: mettingTimeSlot = [] } = {} } = useGetTimeSlotsQuery();

	// Initialize with current month and year
	const currentDate = new Date();
	const [payload, setPayload] = useState({
		date: formatDateForMeeting(currentDate),
		month: currentDate.toLocaleString('default', { month: 'long' }),
		year: currentDate.getFullYear().toString(),
	});

	const { data, refetch } = useGetSeekerAvailabilityQuery(payload); // Pass payload to query
	const previousMonthRef = useRef<string>(payload.month); // Ref to store previous month
	const [selectedSlots, setSelectedSlots] = useState<string[]>([]); // State to manage selected slots
	const [value, setValue] = useState(formatDateForMeeting(new Date()));
	const [highlightDates, setHighlightDates] = useState<string[]>([]);

	// Settings for the slider
	const settings = {
		dots: false,
		infinite: false,
		speed: 500,
		slidesToShow: 9,
		slidesToScroll: 3,
		vertical: true,
		verticalSwiping: true,
		responsive: [
			{
				breakpoint: 767,
				settings: {
					slidesToShow: 6,
				},
			},
			{
				breakpoint: 575,
				settings: {
					slidesToShow: 4,
				},
			},
		],
	};

	type ValuePiece = Date | null;

	// Update payload when the modal opens
	useEffect(() => {
		if (showModal) {
			setPayload({
				date: formatDateForMeeting(currentDate),
				month: currentDate.toLocaleString('default', { month: 'long' }),
				year: currentDate.getFullYear().toString(),
			});
		}
	}, [showModal]);

	// Update selected slots based on fetched data when date or data changes
	useEffect(() => {
		if (data && data.length > 0) {
			const availability = data.find(
				(item: any) => moment(item.date).format('MM/DD/YYYY') === value,
			);
			setHighlightDates(data.map((item: any) => moment(item.date).format('MM/DD/YYYY')));
			setSelectedSlots(availability ? availability.timeSlots : []);
		} else {
			setHighlightDates([]);
		}
	}, [data, value]);

	// Refetch data only when the month changes
	useEffect(() => {
		if (previousMonthRef.current !== payload.month) {
			refetch();
			previousMonthRef.current = payload.month; // Update the ref to the new month
		}
	}, [payload, refetch]);

	const handleTimeSlotClick = (slot: string) => {
		const today = moment().format('MM/DD/YYYY');
		if (today === payload?.date) {
			if (!isTimeGone(slot)) {
				selectSlots(slot);
			}
		} else {
			selectSlots(slot);
		}
	};

	// Pick time slots
	const selectSlots = (slot: any) => {
		if (selectedSlots.includes(slot)) {
			// If slot is already selected, remove it
			setSelectedSlots(selectedSlots.filter((selectedSlot) => selectedSlot !== slot));
		} else {
			// Otherwise, add the slot
			setSelectedSlots([...selectedSlots, slot]);
		}
	};
	// Handle date change
	const handleDateChange = (newValue: any) => {
		const newDate = moment(newValue).toISOString();
		setValue(moment(newDate).format('MM/DD/YYYY'));
		setPayload({
			date: moment(newDate).format('MM/DD/YYYY'),
			month: moment(newDate).format('MMMM'),
			year: moment(newDate).format('YYYY'),
		});
		setHighlightDates(data.map((item: any) => moment(item.date).format('MM/DD/YYYY')));

		const formattedData = data?.filter(
			(res: any) => moment(res.date).format('MM/DD/YYYY') === moment(newDate).format('MM/DD/YYYY'),
		);
		setSelectedSlots(formattedData.length > 0 ? formattedData[0].timeSlots : []);
	};

	// Handle month change
	const handleMonthChange = (newValue: any) => {
		// Get today's date
		const today = new Date();
		today.setHours(0, 0, 0, 0); // Reset time to midnight

		// Get the active start date of the new month (first day of the month)
		const activeStartDate = newValue?.activeStartDate;

		// Check if today's date is within the current month
		const isTodayInCurrentMonth =
			today.getFullYear() === activeStartDate.getFullYear() &&
			today.getMonth() === activeStartDate.getMonth();

		// If today is in the selected month, set value to today; otherwise, set to the active start date of the month
		const newDate = isTodayInCurrentMonth ? today : activeStartDate;
		setValue(newDate); // Set calendar value

		setPayload((prevPayload) => ({
			...prevPayload,
			date: moment(newDate).format('MM/DD/YYYY'),
			month: newDate.toLocaleString('default', { month: 'long' }),
			year: newDate.getFullYear().toString(),
		}));
	};

	// Highlight future dates only
	const highlightTileClassName = ({ date, view }: any) => {
		const today = new Date();
		today.setHours(0, 0, 0, 0); // Reset time to midnight
		if (view === 'month') {
			const formattedDate = formatDateForMeeting(date);
			const isFutureOrToday = date >= today; // Include today and future dates
			if (highlightDates.includes(formattedDate) && isFutureOrToday) {
				return 'highlight-date';
			}
		}
		return date < today ? 'disabled-date' : null; // Only disable dates before today
	};

	// Disable past dates
	const disablePastDates = ({ date }: any) => {
		const today = new Date();
		today.setHours(0, 0, 0, 0); // Set today's time to midnight
		return date < today; // Disable dates before today
	};

	return (
		<Modal.Body>
			<Button
				className="close-btn rounded-circle border-0 fw-bold position-absolute"
				onClick={() => hideCommonModal()}
			>
				<i className="ri-close-line"></i>
			</Button>
			<Form className="edit-form">
				<Stack className="gap-4">
					<div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-2">
						<h4 className="fw-bold mb-0">{translate('Common.setMeetingAvailability')}</h4>
						<div className="profile-content d-flex align-items-center gap-3">
							<div className="flex-1 text-lg-end">
								<h5 className="fw-bold mb-1">{`${firstName} ${lastName}`}</h5>
								<p className="mb-0">{formatAvailableDates(highlightDates, translate)}</p>
							</div>
							<div className="profile-content-img">
								<Image
									src={profilePicture ? `${S3_URL}${profilePicture}` : IMAGE_PATHS.UserDefault}
									alt="profile"
									className="rounded-circle w-100 h-100 object-fit-cover"
									loading="lazy"
								/>
							</div>
						</div>
					</div>

					<div className="select-date-time">
						<div className="d-flex align-items-center justify-content-between gap-2 mb-3">
							<h3 className="mb-0">{translate('Common.selectDateAndTime')}</h3>
							<Button
								onClick={() => modalConfig.onClick(selectedSlots, payload?.date, modalConfig?.id)}
								className="filled-btn rounded-pill lh-1"
								disabled={selectedSlots?.length === 0} // Disable when no slots selected
							>
								{translate('Common.confirm')}
							</Button>
						</div>
						<div className="d-flex flex-column flex-sm-row justify-content-between gap-4">
							<div className="select-time-box">
								<Slider {...settings} className="w-100 overflow-hidden">
									{mettingTimeSlot.length > 0 ? (
										mettingTimeSlot?.map((res: any, index: any) => (
											<div
												key={`${res}-${index}`}
												onClick={() => handleTimeSlotClick(res)}
												style={{ cursor: 'pointer' }}
												className={`time-box rounded-pill bg-white text-center pointer ${selectedSlots.includes(res) ? 'selected' : ''}`}
											>
												{res}
											</div>
										))
									) : (
										<p>{translate('Common.noDataFound')}</p>
									)}
								</Slider>
							</div>
							<div className="date-box">
								<Calendar
									minDate={new Date()}
									onActiveStartDateChange={handleMonthChange}
									onChange={handleDateChange}
									tileClassName={highlightTileClassName}
									tileDisabled={disablePastDates}
									value={value}
								/>
							</div>
						</div>
					</div>
				</Stack>
			</Form>
		</Modal.Body>
	);
};

export default ScheduleMeeting;
